// Video carousel section JavaScript functionality
class VideoCarousel extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  connectedCallback() {
    // 当元素连接到DOM时进行基础初始化
  }

  disconnectedCallback() {
    // 清理观察器
    if (this.buttonObserver) {
      this.buttonObserver.disconnect();
      this.buttonObserver = null;
    }

    // 清理按钮保护定时器
    if (this.buttonProtectionInterval) {
      clearInterval(this.buttonProtectionInterval);
      this.buttonProtectionInterval = null;
    }

    // 清理resize事件监听器
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      window.removeEventListener('orientationchange', this.resizeHandler);
      this.resizeHandler = null;
    }

    // 清理移动端Swiper实例
    if (this.mobileSwiper) {
      this.mobileSwiper.destroy(true, true);
      this.mobileSwiper = null;
    }

    // 清理桌面端Swiper实例
    if (this.desktopSwiper) {
      this.desktopSwiper.destroy(true, true);
      this.desktopSwiper = null;
    }
  }

  init() {
    // 为播放按钮添加点击事件
    this.addEventListener('click', this.handleCardClick.bind(this));
    
    // 延迟初始化，确保DOM完全渲染
    setTimeout(() => {
      // 根据屏幕尺寸选择初始化方式
      if (window.innerWidth >= 768) {
        // 桌面端：只设置轮播功能
        this.setupDesktopCarousel();
      } else {
        // 移动端：只初始化Swiper
        this.initMobileSwiper();
      }
    }, 200);

    // 当页面不可见时暂停所有视频
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseAllPlayingVideos();
      }
    });

    // 监听窗口大小变化 - 实时响应屏幕尺寸变化
    let resizeTimeout;
    const handleResize = () => {
      // 清除之前的计时器
      clearTimeout(resizeTimeout);
      
      // 立即清除之前的尺寸缓存
      this.cardDimensions = null;
      
      // 防抖处理，避免频繁重绘
      resizeTimeout = setTimeout(() => {
        // 根据当前屏幕尺寸选择初始化方式
        if (window.innerWidth >= 768) {
          // 桌面端：只设置轮播功能
          if (!this.desktopSwiper) {
            this.setupDesktopCarousel();
          }
        } else {
          // 移动端：只初始化Swiper
          if (!this.mobileSwiper) {
            this.initMobileSwiper();
          }
        }
        
        // 协调：通知外部边距管理器重新计算
        if (window.marginManager && typeof window.marginManager.applyMargins === 'function') {
          setTimeout(() => window.marginManager.applyMargins(true), 50);
        }
      }, 150);
    };

    window.addEventListener('resize', handleResize);
    
    // 额外监听orientationchange事件（移动设备旋转）
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        handleResize();
      }, 100);
    });

    // 存储resize处理器，用于清理
    this.resizeHandler = handleResize;
  }

  // 设置桌面端Swiper轮播功能
  setupDesktopCarousel() {
    if (window.innerWidth < 768) return;
    
    // 防止重复初始化
    if (this.desktopSwiper && this.desktopSwiper.initialized) {
      console.log('桌面端Swiper已初始化，跳过重复初始化');
      return;
    }

    // 确保移动端Swiper已销毁
    if (this.mobileSwiper) {
      this.mobileSwiper.destroy(true, true);
      this.mobileSwiper = null;
    }

    // 确保桌面端Swiper已销毁
    if (this.desktopSwiper) {
      this.desktopSwiper.destroy(true, true);
      this.desktopSwiper = null;
    }

    // 初始化桌面端Swiper
    this.initDesktopSwiper();
    
    // 强制显示按钮
    this.forceShowButtons();
    
    // 设置轮播逻辑
    this.setupCarouselButtons();

    // 设置按钮保护机制
    this.setupButtonProtection();

    // 延迟再次确保按钮状态正确
    setTimeout(() => {
      this.ensureButtonsActive();
    }, 500);
  }


  // 强制显示按钮
  forceShowButtons() {
    if (window.innerWidth < 768) return;

    const allButtons = this.querySelectorAll('button[is="previous-button"], button[is="next-button"]');
    allButtons.forEach(button => {
      button.style.display = 'block';
      button.style.visibility = 'visible';
      button.style.opacity = '1';
      button.hidden = false;
    });

    const indicators = this.querySelectorAll('.indicators');
    indicators.forEach(indicator => {
      indicator.style.display = 'flex';
      indicator.style.visibility = 'visible';
      indicator.style.opacity = '1';
    });
  }

  // 设置轮播按钮
  setupCarouselButtons() {
    // 查找按钮的多种方式
    let prevButtons = this.querySelectorAll('button[is="previous-button"]');
    let nextButtons = this.querySelectorAll('button[is="next-button"]');
    
    // 如果找不到，尝试其他选择器
    if (prevButtons.length === 0) {
      prevButtons = this.querySelectorAll('.slider__button--prev, [data-direction="previous"], .carousel-prev');
    }
    
    if (nextButtons.length === 0) {
      nextButtons = this.querySelectorAll('.slider__button--next, [data-direction="next"], .carousel-next');
    }

    prevButtons.forEach((button, index) => {
      // 移除disabled状态
      button.disabled = false;
      button.removeAttribute('disabled');
      
      // 强制移除所有现有的事件监听器
      const newButton = button.cloneNode(true);
      // 确保克隆的按钮没有disabled状态
      newButton.disabled = false;
      newButton.removeAttribute('disabled');
      
      if (button.parentNode) {
        button.parentNode.replaceChild(newButton, button);
      }
      
      // 强制确保按钮可见和可点击
      newButton.style.cssText += `
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
        position: relative !important;
        z-index: 999 !important;
      `;
      newButton.disabled = false;
      newButton.hidden = false;
      
      // 添加事件监听器
      const clickHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        
        // 根据屏幕尺寸选择Swiper实例
        if (window.innerWidth >= 768) {
          // 桌面端使用桌面端Swiper
          if (this.desktopSwiper) {
            this.desktopSwiper.slidePrev();
          }
        } else {
          // 移动端使用移动端Swiper
          if (this.mobileSwiper) {
            this.mobileSwiper.slidePrev();
          }
        }
      };
      
      // 只添加click事件，避免重复触发
      newButton.addEventListener('click', clickHandler, { capture: true });
    });

    nextButtons.forEach((button, index) => {
      // 移除disabled状态
      button.disabled = false;
      button.removeAttribute('disabled');
      
      // 强制移除所有现有的事件监听器
      const newButton = button.cloneNode(true);
      if (button.parentNode) {
        button.parentNode.replaceChild(newButton, button);
      }
      
      // 强制确保按钮可见和可点击
      newButton.style.cssText += `
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
        position: relative !important;
        z-index: 999 !important;
      `;
      newButton.disabled = false;
      newButton.hidden = false;
      
      // 添加事件监听器
      const clickHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        
        // 根据屏幕尺寸选择Swiper实例
        if (window.innerWidth >= 768) {
          // 桌面端使用桌面端Swiper
          if (this.desktopSwiper) {
            this.desktopSwiper.slideNext();
          }
        } else {
          // 移动端使用移动端Swiper
          if (this.mobileSwiper) {
            this.mobileSwiper.slideNext();
          }
        }
      };
      
      // 只添加click事件，避免重复触发
      newButton.addEventListener('click', clickHandler, { capture: true });
    });

    // 延迟强制显示按钮
    setTimeout(() => {
      this.forceShowButtons();
    }, 100);
  }



  // 强制在桌面端显示轮播按钮
  forceShowButtons(event) {
    if (window.innerWidth >= 768) {
      // 桌面端：强制显示所有轮播按钮
      this.forceShowButtonsOnDesktop();
    }
  }

  // 设置按钮保护机制，防止被外部代码禁用
  setupButtonProtection() {
    if (window.innerWidth < 768) return;

    const prevButtons = this.querySelectorAll('button[is="previous-button"]');
    const nextButtons = this.querySelectorAll('button[is="next-button"]');
    
    [...prevButtons, ...nextButtons].forEach(button => {
      // 重写 disabled 属性，防止被设置为 true
      Object.defineProperty(button, 'disabled', {
        get: function() { return false; },
        set: function(value) { 
          // 忽略所有试图禁用按钮的操作
          console.log('🛡️ Prevented button disable attempt');
        },
        configurable: true
      });

      // 重写 setAttribute，防止通过 setAttribute 禁用
      const originalSetAttribute = button.setAttribute;
      button.setAttribute = function(name, value) {
        if (name === 'disabled') {
          console.log('🛡️ Prevented disabled attribute setting');
          return;
        }
        originalSetAttribute.call(this, name, value);
      };

      // 重写 removeAttribute 的行为
      const originalRemoveAttribute = button.removeAttribute;
      button.removeAttribute = function(name) {
        originalRemoveAttribute.call(this, name);
        if (name === 'disabled') {
          this.disabled = false; // 确保始终保持启用状态
        }
      };
    });

    // 定期检查和修复按钮状态
    if (this.buttonProtectionInterval) {
      clearInterval(this.buttonProtectionInterval);
    }
    
    this.buttonProtectionInterval = setInterval(() => {
      this.ensureButtonsActive();
    }, 1000);
  }

  // 确保按钮始终处于可点击状态
  ensureButtonsActive() {
    if (window.innerWidth < 768) return;

    const prevButtons = this.querySelectorAll('button[is="previous-button"]');
    const nextButtons = this.querySelectorAll('button[is="next-button"]');
    
    [...prevButtons, ...nextButtons].forEach(button => {
      // 强制移除 disabled 属性
      button.removeAttribute('disabled');
      
      // 强制设置样式
      button.style.cssText += `
        pointer-events: auto !important;
        opacity: 1 !important;
        display: block !important;
        visibility: visible !important;
      `;
      
      button.hidden = false;
      
      // 确保有点击事件监听器
      if (!button.dataset.hasCustomListener) {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          // 根据屏幕尺寸选择Swiper实例
          if (window.innerWidth >= 768) {
            // 桌面端使用桌面端Swiper
            if (this.desktopSwiper) {
            const isNext = button.getAttribute('is') === 'next-button';
              if (isNext) {
                this.desktopSwiper.slideNext();
              } else {
                this.desktopSwiper.slidePrev();
              }
            }
          } else {
            // 移动端使用移动端Swiper
            if (this.mobileSwiper) {
              const isNext = button.getAttribute('is') === 'next-button';
              if (isNext) {
                this.mobileSwiper.slideNext();
              } else {
                this.mobileSwiper.slidePrev();
              }
            }
          }
        }, { capture: true });
        
        button.dataset.hasCustomListener = 'true';
      }
    });
  }

  // 强制在桌面端显示按钮的方法
  forceShowButtonsOnDesktop() {
    if (window.innerWidth >= 768) {
      const prevButtons = this.querySelectorAll('button[is="previous-button"]');
      const nextButtons = this.querySelectorAll('button[is="next-button"]');
      
      prevButtons.forEach(button => {
        button.style.display = 'block';
        button.style.visibility = 'visible';
        button.style.opacity = '1';
        button.hidden = false;
        // 不强制设置disabled状态，让SliderElement管理
      });
      
      nextButtons.forEach(button => {
        button.style.display = 'block';
        button.style.visibility = 'visible';
        button.style.opacity = '1';
        button.hidden = false;
        // 不强制设置disabled状态，让SliderElement管理
      });

      // 设置MutationObserver监听按钮状态变化
      if (!this.buttonObserver) {
        this.buttonObserver = new MutationObserver((mutations) => {
          if (window.innerWidth >= 768) {
            mutations.forEach((mutation) => {
              if (mutation.type === 'attributes' && 
                  (mutation.attributeName === 'hidden' || 
                   mutation.attributeName === 'style')) {
                const button = mutation.target;
                if (button.tagName === 'BUTTON' && 
                    (button.hasAttribute('is') && 
                     (button.getAttribute('is') === 'previous-button' || 
                      button.getAttribute('is') === 'next-button'))) {
                  // 强制重新显示按钮，但不改变disabled状态
                  setTimeout(() => {
                    button.style.display = 'block';
                    button.style.visibility = 'visible';
                    button.style.opacity = '1';
                    button.hidden = false;
                  }, 10);
                }
              }
            });
          }
        });

        // 开始观察按钮的属性变化，但不监听disabled属性
        [...prevButtons, ...nextButtons].forEach(button => {
          this.buttonObserver.observe(button, { 
            attributes: true, 
            attributeFilter: ['hidden', 'style', 'class'] 
          });
        });
      }
    }
  }



  handleCardClick(event) {
    const playButton = event.target.closest('.play-button');
    const card = event.target.closest('.video-carousel-card');
    const videoThumbnail = event.target.closest('.video-thumbnail');
    
    if (card) {
      event.preventDefault();
      event.stopPropagation();
      
      // 如果点击的是播放按钮，播放视频
      if (playButton) {
        const videoUrl = playButton.dataset.videoUrl;
        const videoId = playButton.dataset.video;
        const videoSources = playButton.dataset.videoSources;
        
        // 已移除调试信息
        
        if (videoUrl && this.isExternalVideo(videoUrl)) {
          // YouTube/Vimeo 外部视频
          this.playExternalVideo(card, videoUrl);
        } else if (videoSources || videoId) {
          // Shopify 上传的视频
          this.playShopifyVideo(card, videoSources || videoId);
        } else {
          console.warn('No valid video source found');
        }
      } 
      // 如果点击的是正在播放的视频区域，重置为缩略图
      else if (videoThumbnail && card.classList.contains('video-playing')) {
        this.resetVideoToThumbnail(card);
      }
    }
  }

  resetVideoToThumbnail(card) {
    // 移除播放状态
    card.classList.remove('video-playing');
    
    // 恢复原始缩略图
    const thumbnailContainer = card.querySelector('.video-thumbnail');
    if (thumbnailContainer && card.dataset.originalThumbnail) {
      thumbnailContainer.innerHTML = card.dataset.originalThumbnail;
    }
  }

  isExternalVideo(url) {
    return url.includes('youtube.com') || 
           url.includes('youtu.be') || 
           url.includes('vimeo.com');
  }

  playExternalVideo(card, videoUrl) {
    // 获取视频缩略图容器
    const thumbnailContainer = card.querySelector('.video-thumbnail');
    if (!thumbnailContainer) return;

    // 保存原始内容
    if (!card.dataset.originalThumbnail) {
      card.dataset.originalThumbnail = thumbnailContainer.innerHTML;
    }

    let embedUrl = '';
    
    // 检测YouTube视频
    if (videoUrl.includes('youtube.com/watch?v=')) {
      const videoId = videoUrl.split('v=')[1].split('&')[0];
      embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`;
    } else if (videoUrl.includes('youtu.be/')) {
      const videoId = videoUrl.split('youtu.be/')[1].split('?')[0];
      embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`;
    }
    // 检测Vimeo视频
    else if (videoUrl.includes('vimeo.com/')) {
      const videoId = videoUrl.split('vimeo.com/')[1].split('?')[0];
      embedUrl = `https://player.vimeo.com/video/${videoId}?autoplay=1`;
    }

    if (!embedUrl) {
      console.warn('Unsupported video URL:', videoUrl);
      return;
    }

    // 创建iframe
    const iframe = document.createElement('iframe');
    iframe.src = embedUrl;
    iframe.frameBorder = '0';
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border: none;
    `;

    // 替换缩略图内容为iframe
    thumbnailContainer.innerHTML = '';
    thumbnailContainer.appendChild(iframe);
    
    // 添加播放状态类
    card.classList.add('video-playing');
  }

  playShopifyVideo(card, videoSourcesOrId) {
    // 获取视频缩略图容器
    const thumbnailContainer = card.querySelector('.video-thumbnail');
    if (!thumbnailContainer) return;

    // 保存原始内容
    if (!card.dataset.originalThumbnail) {
      card.dataset.originalThumbnail = thumbnailContainer.innerHTML;
    }

    // 创建HTML5 video元素
    const video = document.createElement('video');
    video.controls = true;
    video.autoplay = true;
    video.muted = this.getAttribute('data-enable-video-muted') === 'true';
    video.loop = this.getAttribute('data-enable-video-looping') === 'true';
    video.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      border: none;
    `;

    // 解析视频源
    let videoSources = [];
    
    if (videoSourcesOrId && videoSourcesOrId.includes('|')) {
      // 如果包含|，说明是多个URL
      videoSources = videoSourcesOrId.split('|').filter(url => url.trim());
    } else if (videoSourcesOrId && videoSourcesOrId.startsWith('http')) {
      // 单个完整URL
      videoSources = [videoSourcesOrId];
    } else if (videoSourcesOrId) {
      // 视频ID，需要构建URL
      this.tryAlternativeVideoFormats(video, videoSourcesOrId);
      thumbnailContainer.innerHTML = '';
      thumbnailContainer.appendChild(video);
      card.classList.add('video-playing');
      return;
    }

    if (videoSources.length === 0) {
      console.error('No valid video sources found');
      this.showVideoError(thumbnailContainer);
      return;
    }

    // 尝试加载第一个视频源
    this.loadVideoWithSources(video, videoSources, 0, thumbnailContainer);
    
    // 替换缩略图内容为video
    thumbnailContainer.innerHTML = '';
    thumbnailContainer.appendChild(video);
    
    // 添加播放状态类
    card.classList.add('video-playing');
  }

  loadVideoWithSources(video, sources, currentIndex, container) {
    if (currentIndex >= sources.length) {
      this.showVideoError(container);
      return;
    }

    const currentSource = sources[currentIndex];
    
    // 移除之前的错误监听器
    video.removeEventListener('error', video._errorHandler);
    video.removeEventListener('loadeddata', video._loadedHandler);
    
    // 添加新的错误处理器
    video._errorHandler = (e) => {
      this.loadVideoWithSources(video, sources, currentIndex + 1, container);
    };
    
    // 添加成功加载处理器
    video._loadedHandler = () => {
      // 视频加载成功
    };
    
    video.addEventListener('error', video._errorHandler, { once: true });
    video.addEventListener('loadeddata', video._loadedHandler, { once: true });
    
    // 设置视频源
    video.src = currentSource;
  }

  tryAlternativeVideoFormats(videoElement, videoId) {
    
    // 尝试不同的Shopify视频URL格式
    const formats = [
      `https://cdn.shopify.com/videos/${videoId}`,
      `https://cdn.shopify.com/s/files/1/videos/${videoId}.mp4`,
      `https://cdn.shopify.com/s/files/1/videos/${videoId}.webm`,
      `https://cdn.shopify.com/s/files/1/videos/${videoId}/video.mp4`,
      `https://cdn.shopify.com/videos/c/o/v/e/r/${videoId}`,
      // 尝试不同的文件扩展名
      `https://cdn.shopify.com/videos/${videoId}.mp4`,
      `https://cdn.shopify.com/videos/${videoId}.webm`
    ];

    this.loadVideoWithSources(videoElement, formats, 0, videoElement.parentNode);
  }

  showVideoError(container) {
    // 显示错误消息
    const errorDiv = document.createElement('div');
    errorDiv.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: white;
      background: rgba(0,0,0,0.8);
      padding: 15px;
      border-radius: 8px;
      text-align: center;
      font-size: 14px;
      max-width: 200px;
      z-index: 10;
    `;
    errorDiv.innerHTML = `
      <div style="margin-bottom: 8px;">⚠️</div>
      <div>Video unavailable</div>
      <div style="font-size: 12px; opacity: 0.8; margin-top: 4px;">Check console for details</div>
    `;
    container.appendChild(errorDiv);
  }

  onSliderChange(event) {
    // 当滑块切换时，暂停所有正在播放的视频
    this.pauseAllPlayingVideos();
  }

  pauseAllPlayingVideos() {
    // 查找所有正在播放的视频
    const playingCards = this.querySelectorAll('.video-playing');
    playingCards.forEach(card => {
      const iframe = card.querySelector('iframe');
      const video = card.querySelector('video');
      
      if (iframe) {
        // 通过重置src来停止iframe视频播放
        const src = iframe.src;
        iframe.src = '';
        iframe.src = src.replace('autoplay=1', 'autoplay=0');
      } else if (video) {
        // 暂停HTML5视频
        video.pause();
      }
      
      // 移除播放状态类
      card.classList.remove('video-playing');
    });
  }

  // 初始化桌面端Swiper
  initDesktopSwiper() {
    // 只在桌面端初始化Swiper
    if (window.innerWidth < 768) return;
    
    // 检查Swiper是否已加载
    if (typeof Swiper === 'undefined') {
      // 如果Swiper未加载，延迟重试
      setTimeout(() => {
        this.initDesktopSwiper();
      }, 500);
      return;
    }
    
    // 等待DOM完全渲染
    setTimeout(() => {
      this.initDesktopSwiperInternal();
    }, 200);
  }
  
  // 内部初始化方法
  initDesktopSwiperInternal() {

    const sectionId = this.closest('section')?.id || '';
    const realSectionId = sectionId.replace('shopify-section-', '');
    let finalSwiperContainer = this.querySelector(`#DesktopSwiper-${realSectionId}`);
    
    // 如果按ID找不到，尝试直接通过类名查找
    if (!finalSwiperContainer) {
      finalSwiperContainer = this.querySelector('.desktop-carousel.swiper');
    }
    
    if (!finalSwiperContainer) {
      return;
    }

    // 销毁已存在的桌面端Swiper实例
    if (this.desktopSwiper) {
      this.desktopSwiper.destroy(true, true);
      this.desktopSwiper = null;
    }

    try {
      // 获取用户设置的桌面端列数
      const desktopColumns = parseInt(this.getAttribute('data-desktop-columns')) || 3;
      
      // 预先设置slide宽度，确保Swiper正确计算
      const slides = finalSwiperContainer.querySelectorAll('.swiper-slide');
      const slideWidth = `calc((100% - 24px) / 4)`;
      slides.forEach(slide => {
        slide.style.setProperty('width', slideWidth, 'important');
        slide.style.setProperty('max-width', slideWidth, 'important');
        slide.style.setProperty('min-width', slideWidth, 'important');
      });
      
      // 初始化桌面端Swiper
      this.desktopSwiper = new Swiper(finalSwiperContainer, {
        // 基本配置 - 一屏显示4个视频
        slidesPerView: 4,
        spaceBetween: 8,
        centeredSlides: false,
        autoHeight: true,
        watchOverflow: true,
        
        // 滑动配置 - 每次滑动一个视频
        slidesPerGroup: 1,
        
        // 确保每次只滑动一个
        allowTouchMove: true,
        simulateTouch: true,
        
        // 确保正确显示4个视频
        width: null,
        setWrapperSize: true,
        
        // 强制设置slide宽度
        slideClass: 'swiper-slide',
        slideActiveClass: 'swiper-slide-active',
        slideVisibleClass: 'swiper-slide-visible',
        
        // 强制设置slide宽度计算
        observer: true,
        observeParents: true,
        
        // 触摸配置
        touchRatio: 1,
        touchAngle: 45,
        grabCursor: true,
        allowTouchMove: true,
        simulateTouch: true,
        passiveListeners: false,
        
        // 性能优化
        speed: 3000, // 默认3秒，将通过设置项覆盖
        preloadImages: false,
        lazy: {
          loadPrevNext: true,
        },
        // 事件监听
        on: {
          slideChange: () => {
            // 当滑动时暂停所有视频
            this.pauseAllPlayingVideos();
          }
        }
      });

      // 为桌面端Swiper添加视频播放事件
      finalSwiperContainer.addEventListener('click', this.handleCardClick.bind(this));
      
      // 获取滑动持续时间设置
      const slideDuration = parseFloat(this.getAttribute('data-slide-duration')) || 3.0;
      const slideDurationMs = slideDuration * 1000;
      
      // 强制设置Swiper配置
      if (this.desktopSwiper) {
        this.desktopSwiper.params.slidesPerView = 4;
        this.desktopSwiper.params.slidesPerGroup = 1;
        this.desktopSwiper.params.speed = slideDurationMs; // 使用设置的速度
        this.desktopSwiper.update();
        this.desktopSwiper.initialized = true; // 标记为已初始化
        
        // 强制覆盖CSS过渡时间
        const swiperWrapper = finalSwiperContainer.querySelector('.swiper-wrapper');
        if (swiperWrapper) {
          swiperWrapper.style.transition = `transform ${slideDuration}s cubic-bezier(0.6, 0, 0.4, 1) !important`;
        }
      }

      // 强制更新Swiper布局，确保正确显示
      setTimeout(() => {
        if (this.desktopSwiper && this.desktopSwiper.update) {
          // 先强制设置slide宽度
          const slides = finalSwiperContainer.querySelectorAll('.swiper-slide');
          const slideWidth = `calc((100% -24px) / 4)`;
          slides.forEach(slide => {
            slide.style.setProperty('width', slideWidth, 'important');
            slide.style.setProperty('max-width', slideWidth, 'important');
            slide.style.setProperty('min-width', slideWidth, 'important');
          });
          
          // 然后更新Swiper
          this.desktopSwiper.update();
          this.desktopSwiper.updateSlides();
          this.desktopSwiper.updateProgress();
          this.desktopSwiper.updateSlidesClasses();
          
          // 强制设置过渡时间
          const swiperWrapper = finalSwiperContainer.querySelector('.swiper-wrapper');
          if (swiperWrapper) {
            swiperWrapper.style.transition = `transform ${slideDuration}s cubic-bezier(0.6, 0, 0.4, 1) !important`;
          }
          
          // 再次强制设置宽度以确保生效
          slides.forEach(slide => {
            slide.style.setProperty('width', slideWidth, 'important');
            slide.style.setProperty('max-width', slideWidth, 'important');
            slide.style.setProperty('min-width', slideWidth, 'important');
          });
        }
      }, 50);

    } catch (error) {
      console.error('桌面端Swiper初始化失败:', error);
    }
    
          // 调试信息
      if (this.desktopSwiper) {
        console.log('桌面端Swiper初始化成功:', {
          slidesPerView: this.desktopSwiper.params.slidesPerView,
          spaceBetween: this.desktopSwiper.params.spaceBetween,
          slidesLength: this.desktopSwiper.slides.length,
          containerWidth: finalSwiperContainer.offsetWidth,
          visibleSlides: this.desktopSwiper.slidesPerView,
          slidesPerGroup: this.desktopSwiper.params.slidesPerGroup,
          speed: this.desktopSwiper.params.speed
        });
      
      // 检查每个slide的宽度
      const slides = finalSwiperContainer.querySelectorAll('.swiper-slide');
      slides.forEach((slide, index) => {
        const computedStyle = window.getComputedStyle(slide);
      });
      
      // 检查容器宽度
      console.log('Container width:', finalSwiperContainer.offsetWidth);
      console.log('Expected slide width:', `calc((100% -24px) / 4)`);
      
      // 强制设置slide宽度
      const slideWidth = `calc((100% -24px) / 4)`;
      slides.forEach((slide, index) => {
        slide.style.setProperty('width', slideWidth, 'important');
        slide.style.setProperty('max-width', slideWidth, 'important');
        slide.style.setProperty('min-width', slideWidth, 'important');
        console.log(`Forced slide ${index + 1} width to:`, slideWidth);
      });
      
    }
  }

  // 初始化移动端Swiper
  initMobileSwiper() {
    // 只在移动端初始化Swiper
    if (window.innerWidth >= 768) return;
    
    // 检查Swiper是否已加载
    if (typeof Swiper === 'undefined') {
      // 如果Swiper未加载，延迟重试
      setTimeout(() => {
        this.initMobileSwiper();
      }, 500);
      return;
    }

    const sectionId = this.closest('section')?.id || '';
    const realSectionId = sectionId.replace('shopify-section-', '');
    let finalSwiperContainer = this.querySelector(`#MobileSwiper-${realSectionId}`);
    
    // 如果按ID找不到，尝试直接通过类名查找
    if (!finalSwiperContainer) {
      finalSwiperContainer = this.querySelector('.mobile-carousel.swiper');
    }
    
    if (!finalSwiperContainer) {
      return;
    }

    // 销毁已存在的Swiper实例
    if (this.mobileSwiper) {
      this.mobileSwiper.destroy(true, true);
      this.mobileSwiper = null;
    }

    try {
      // 初始化Swiper
      this.mobileSwiper = new Swiper(finalSwiperContainer, {
        // 基本配置
        slidesPerView: 1,
        spaceBetween: 0,
        centeredSlides: true,
        autoHeight: true, // 自适应高度，避免上下滚动条
        watchOverflow: true, // 当slides不足时禁用swiper
        
        // 触摸配置
        touchRatio: 1,
        touchAngle: 45,
        grabCursor: true,
        allowTouchMove: true,
        simulateTouch: true,
        passiveListeners: false,
        
        // 分页
        pagination: {
          el: finalSwiperContainer.querySelector('.swiper-pagination'),
          clickable: true,
          dynamicBullets: false
        },
        
        // 性能优化
        speed: 300, // 移动端保持快速切换
        preloadImages: false,
        lazy: {
          loadPrevNext: true,
        },
        
        // 响应式breakpoints - 移动端统一配置
        breakpoints: {
          320: {
            slidesPerView: 1,
            spaceBetween: 8,
            centeredSlides: true,
            autoHeight: true
          },
          480: {
            slidesPerView: 1,
            spaceBetween: 8,
            centeredSlides: true,
            autoHeight: true
          },
          1023: {
            slidesPerView: 1,
            spaceBetween: 8,
            centeredSlides: true,
            autoHeight: true
          }
        },

        // 事件监听
        on: {
          slideChange: () => {
            // 当滑动时暂停所有视频
            this.pauseAllPlayingVideos();
          }
        }
      });

      // 为移动端Swiper添加视频播放事件
      finalSwiperContainer.addEventListener('click', this.handleCardClick.bind(this));

      // 强制更新Swiper布局，确保正确显示
      setTimeout(() => {
        if (this.mobileSwiper && this.mobileSwiper.update) {
          this.mobileSwiper.update();
          this.mobileSwiper.updateAutoHeight(300); // 300ms动画过渡，与切换速度保持一致
        }
      }, 100);

    } catch (error) {
      console.error('Swiper初始化失败:', error);
    }
  }


}

// 注册自定义元素
if (!customElements.get('video-carousel')) {
  customElements.define('video-carousel', VideoCarousel);
}


// 额外的进度条样式增强和隐藏滚动条，优化滚动性能
const style = document.createElement('style');
style.textContent = `
  /* 隐藏webkit浏览器的滚动条 */
  video-carousel motion-list::-webkit-scrollbar {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    background: transparent !important;
  }
  
  /* 兼容性隐藏和性能优化 */
  video-carousel motion-list {
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;
    scroll-behavior: smooth !important;
    will-change: scroll-position !important;
    scroll-snap-type: x mandatory !important;
  }
  
  /* 优化卡片的滚动对齐 */
  video-carousel .video-carousel-card {
    scroll-snap-align: start !important;
    will-change: transform !important;
  }
  
  /* 优化滚动性能 */
  video-carousel * {
    backface-visibility: hidden !important;
    -webkit-backface-visibility: hidden !important;
  }
  
  .video-carousel-video.playing .progress-bar {
    opacity: 1;
  }
  
  .video-carousel-video .progress-bar {
    opacity: 0.7;
    transition: opacity 0.3s ease;
  }
  
  .video-carousel-video:hover .progress-bar {
    opacity: 1;
  }
  
  .progress-bar-fill {
    background: linear-gradient(90deg, #007bff 0%, #0056b3 100%);
    box-shadow: 0 0 10px rgba(0, 123, 255, 0.3);
  }
  
  .video-carousel-card:hover .progress-bar-fill {
    box-shadow: 0 0 15px rgba(0, 123, 255, 0.5);
  }
`;

document.head.appendChild(style);
